import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTable } from '@/components/DataTable'
import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import type { AdminPoolEntry, PoolHealthStatus } from '@/lib/admin/admin'
import { getPoolHealthOverview } from '@/lib/admin/server'

export const Route = createFileRoute('/_authenticated/admin/pool')({
  component: AdminPoolPage,
})

// Aggregate counters lag the API's own meter interval; matches the cadence
// other admin pages poll live control-plane data at.
const REFETCH_INTERVAL_MS = 15_000

// Pools run into the thousands of upstream proxies; paginate client-side
// rather than rendering every row.
const PAGE_SIZE = 50

/** Badge copy/variant for each `PoolHealthStatus` — kept exhaustive so a new
 * status value can't silently fall through to the wrong badge. */
const STATUS_BADGE: Record<
  PoolHealthStatus,
  { label: string; variant: 'secondary' | 'destructive' }
> = {
  unreachable: { label: 'Unreachable', variant: 'destructive' },
  disabled: { label: 'Disabled', variant: 'secondary' },
  healthy: { label: 'Healthy', variant: 'secondary' },
  degraded: { label: 'Degraded', variant: 'destructive' },
  critical: { label: 'Critical', variant: 'destructive' },
}

const columns: ColumnDef<AdminPoolEntry>[] = [
  {
    accessorKey: 'id',
    header: 'Proxy',
    enableSorting: true,
    cell: ({ row }) => <span className="font-medium">{row.original.id}</span>,
  },
  {
    accessorKey: 'location',
    header: 'Location',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {row.original.location ?? 'Unknown'}
      </span>
    ),
  },
  {
    // Sort by liveness so "down first" is expressible as an ascending sort
    // (false < true) — matches the page's default of surfacing problems.
    id: 'status',
    accessorFn: (e) => e.alive,
    header: 'Status',
    enableSorting: true,
    cell: ({ row }) => {
      const entry = row.original
      return entry.alive ? (
        <Badge variant="secondary">alive</Badge>
      ) : (
        <div className="flex flex-col gap-0.5">
          <Badge variant="destructive">down</Badge>
          {entry.downReason ? (
            <span className="text-xs text-muted-foreground">
              {entry.downReason}
            </span>
          ) : null}
        </div>
      )
    },
  },
  {
    accessorKey: 'stickyOk',
    header: 'Sticky',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="text-muted-foreground">
        {row.original.stickyOk ? 'yes' : 'no'}
      </span>
    ),
  },
  {
    accessorKey: 'usageCount',
    header: 'Usage count',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="tabular-nums">{row.original.usageCount}</span>
    ),
  },
  {
    accessorKey: 'successRate',
    header: 'Success rate',
    enableSorting: true,
    cell: ({ row }) => (
      <span className="tabular-nums">
        {Math.round(row.original.successRate * 100)}%
      </span>
    ),
  },
]

function AdminPoolPage() {
  const { data, isPending } = useQuery({
    queryKey: ['admin-pool-health'],
    queryFn: () => getPoolHealthOverview(),
    refetchInterval: REFETCH_INTERVAL_MS,
  })

  const unreachable = !isPending && data && !data.reachable

  const statusBadge = isPending ? (
    <Skeleton className="h-5 w-24" />
  ) : data ? (
    <Badge
      variant={STATUS_BADGE[data.status].variant}
      className={data.status === 'critical' ? 'font-semibold' : undefined}
    >
      {STATUS_BADGE[data.status].label}
    </Badge>
  ) : null

  const stats = [
    {
      label: 'Healthy / total',
      value:
        isPending || !data ? null : `${data.healthyCount} / ${data.totalCount}`,
    },
    {
      label: 'Unhealthy',
      value: isPending || !data ? null : data.unhealthyCount,
    },
    {
      label: 'Upstream proxy',
      value:
        isPending || !data
          ? null
          : data.upstreamProxyEnabled
            ? 'Enabled'
            : 'Disabled',
    },
  ]

  const entryCount = data?.entries?.length ?? 0

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Pool health"
        description="Live status of the shared proxy pool."
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardDescription>Status</CardDescription>
            <CardTitle className="text-2xl">{statusBadge}</CardTitle>
          </CardHeader>
        </Card>
        {stats.map((s) => (
          <Card key={s.label}>
            <CardHeader>
              <CardDescription>{s.label}</CardDescription>
              <CardTitle className="text-2xl tabular-nums">{s.value}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upstream proxies</CardTitle>
          <CardDescription>
            {isPending
              ? 'Loading…'
              : unreachable
                ? 'The proxy control plane could not be reached.'
                : `${entryCount} upstream${entryCount === 1 ? '' : 's'}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={unreachable ? [] : data?.entries}
            isPending={isPending}
            emptyMessage={
              unreachable
                ? 'Pool status is unavailable right now.'
                : 'No upstream proxies configured.'
            }
            pageSize={PAGE_SIZE}
            initialSorting={[{ id: 'status', desc: false }]}
          />
        </CardContent>
      </Card>
    </div>
  )
}
